import React, { useState, useEffect, useMemo, useRef } from 'react'
import classNames from 'classnames'
import { Form as RBForm } from 'react-bootstrap'
import Imgix from 'react-imgix'
import axios from 'axios'
import {
  useErrorService,
  useNotificationService,
  useResolutionService,
} from '@abroad/components'
import useDebounce from '../common/useDebounce'
import API from '../../utils/API'
import { useParams } from 'react-router-dom'
import ExperienceDeleteIcon from './ExperienceDeleteIcon'
import ExperienceButton from './ExperienceButton'
import ImageCropModal from '../common/ImageCropModal'
import ExperienceCKEditor from './ExperienceCKEditor'
import { ExperienceSelect } from '../form'
import ExperienceConfirmModal from './ExperienceConfirmModal'
import LearningAttachedDndRow from './LearningAttachedDndRow'

const BLOG_ROW_DND = 'LEARNING_READ_ROW'

const emptyBlogDraft = () => ({
  title: '',
  author: '',
  subTitle: '',
  type: 'written',
  blog: '',
  blogLink: '',
  image: '',
})

const EMPTY_BLOG_FIELD_ERRORS = { title: '', blogLink: '' }

/** Same rule as CreateBlog / orientation: http(s) only (blocks javascript:). */
const isValidHttpUrl = (value) => {
  try {
    const url = new URL(value)
    return url.protocol === 'http:' || url.protocol === 'https:'
  } catch (e) {
    return false
  }
}

const mediaId = (item) => item?._id || item?.id

const truncateLabel = (text) => {
  const t = String(text || '').trim()
  if (t.length > 72) return `${t.slice(0, 69)}\u2026`
  return t
}

const normalizeBlog = (item) => {
  if (!item) return item
  const id = mediaId(item)
  return id ? { ...item, _id: id, id } : item
}

const blogSubtitle = (blog) => {
  const parts = []
  if (blog?.author) {
    parts.push(blog.author)
  }
  if (blog?.subTitle) {
    parts.push(blog.subTitle)
  }
  return parts.join(' · ')
}

const QuestBlogs = ({
  blogs = [],
  disabled = false,
  addBlog,
  onBlogUpdate,
  onSaveStateChange,
  onRefetchResources,
  layout = 'legacy',
  placeholder = 'Search article title…',
}) => {
  const Error = useErrorService()
  const Notification = useNotificationService()
  const { isMDScreen } = useResolutionService()
  const { questId } = useParams()
  const [questBlogs, setQuestBlogs] = useState([...blogs, {}])
  const [allBlogs, setAllBlogs] = useState([])
  const [searchData, setSearchData] = useState([])
  const [inputFocus, setInputFocus] = useState(false)
  const [currentIndex, setCurrentIndex] = useState()
  const [localSearch, setLocalSearch] = useState('')
  const [showCreate, setShowCreate] = useState(false)
  const [editingId, setEditingId] = useState(null)
  const [draft, setDraft] = useState(emptyBlogDraft())
  const [fieldErrors, setFieldErrors] = useState(EMPTY_BLOG_FIELD_ERRORS)
  const [isSaving, setIsSaving] = useState(false)
  const [isUploading, setIsUploading] = useState(false)
  const [isReorderBusy, setIsReorderBusy] = useState(false)
  const [showRemoveImageConfirm, setShowRemoveImageConfirm] = useState(false)
  const [pendingRemoveIndex, setPendingRemoveIndex] = useState(null)
  const [isDraggingImage, setIsDraggingImage] = useState(false)
  const [showImageCropModal, setShowImageCropModal] = useState(false)
  const [selectedImageFile, setSelectedImageFile] = useState(null)
  const imageRef = useRef(null)
  const questBlogsRef = useRef([])
  const isReorderingRef = useRef(false)
  const lastSavedOrderRef = useRef('')
  const debouncedSearch = useDebounce(localSearch, 400)

  const listDisabled = disabled || isReorderBusy

  const getOrderKey = (list) =>
    (list || [])
      .map((blog) => mediaId(blog))
      .filter(Boolean)
      .join(',')

  const setQuestBlogsSynced = (updater) => {
    setQuestBlogs((prev) => {
      const next = typeof updater === 'function' ? updater(prev) : updater
      questBlogsRef.current = next
      return next
    })
  }

  useEffect(() => {
    if (layout === 'picker') {
      if (isReorderingRef.current || isReorderBusy) return
      const next = blogs.filter((blog) => mediaId(blog)).map(normalizeBlog)
      setQuestBlogsSynced(next)
      lastSavedOrderRef.current = getOrderKey(next)
      return
    }
    setQuestBlogs([...blogs, {}])
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [blogs, layout])

  useEffect(() => {
    const getAllBlogs = async () => {
      try {
        const { data } = await API.AdminAPI.quests.getBlogAll()
        setAllBlogs((data || []).map(normalizeBlog))
      } catch (e) {}
    }
    getAllBlogs()
  }, [])

  useEffect(() => {
    if (layout === 'picker') {
      const attachedIds = new Set(questBlogs.map((blog) => mediaId(blog)))
      const query = debouncedSearch?.trim()?.toLowerCase() || ''
      const filtered = allBlogs.filter((blog) => {
        const id = mediaId(blog)
        if (attachedIds.has(id)) {
          return false
        }
        if (!query) {
          return true
        }
        return (
          blog?.title?.toLowerCase()?.includes(query) ||
          blog?.subTitle?.toLowerCase()?.includes(query) ||
          blog?.author?.toLowerCase()?.includes(query)
        )
      })
      setSearchData(filtered)
      return
    }

    if (localSearch !== undefined) {
      const s = allBlogs.filter(
        (blog) =>
          blog?.title
            ?.toLowerCase()
            ?.includes(debouncedSearch?.toLowerCase()) ||
          blog?.subTitle
            ?.toLowerCase()
            ?.includes(debouncedSearch?.toLowerCase()),
      )
      setSearchData(s)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch, allBlogs, questBlogs, layout])

  const handleChange = (i, event) => {
    let values = [...questBlogs]
    values[i] = {
      ...values[i],
      title: event.target.value,
    }
    setCurrentIndex(i)
    setQuestBlogs(values)
  }

  const addClick = async (blog, type) => {
    const id = mediaId(blog)
    if (typeof onSaveStateChange === 'function') {
      onSaveStateChange('saving')
    }
    try {
      await API.AdminAPI.quests.toggleBlogMedia(questId, id, {
        attach: type === 'add',
      })
      if (type === 'add') {
        addBlog && addBlog(normalizeBlog(blog))
      }
      if (typeof onSaveStateChange === 'function') {
        onSaveStateChange('saved')
      }
    } catch (e) {
      Error.showError(e)
      if (typeof onSaveStateChange === 'function') {
        onSaveStateChange('error')
      }
      throw e
    }
  }

  const removeClick = async (i) => {
    let values = [...questBlogs]
    if (values.length !== 1 || layout === 'picker') {
      const removed = values[i]
      try {
        await addClick(removed, 'remove')
      } catch (e) {
        return
      }
      values.splice(i, 1)
      setQuestBlogsSynced(values)
      lastSavedOrderRef.current = getOrderKey(values)
      if (editingId && editingId === mediaId(removed)) {
        closeForm()
      }
      if (onBlogUpdate) {
        onBlogUpdate(values)
      }
    }
  }

  const restoreBlogsAfterReorderFailure = async (orderBefore) => {
    if (typeof onRefetchResources === 'function') {
      try {
        const data = await onRefetchResources()
        if (data?.blogs) {
          const restored = data.blogs
            .filter((blog) => mediaId(blog))
            .map(normalizeBlog)
          setQuestBlogsSynced(restored)
          lastSavedOrderRef.current = getOrderKey(restored)
          return
        }
      } catch (e) {
        // Fall through to local restore.
      }
    }
    setQuestBlogsSynced(orderBefore)
    lastSavedOrderRef.current = getOrderKey(orderBefore)
    if (onBlogUpdate) {
      onBlogUpdate(orderBefore)
    }
  }

  const handleReorderDrop = async ({ id, fromIndex, toIndex }) => {
    if (listDisabled) return
    const before = [...(questBlogsRef.current || [])]
    const from = before.findIndex((blog) => mediaId(blog) === id)
    if (from < 0) return
    const to =
      typeof toIndex === 'number' && toIndex >= 0 && toIndex < before.length
        ? toIndex
        : fromIndex
    if (from === to) return

    const next = [...before]
    const [moved] = next.splice(from, 1)
    next.splice(to, 0, moved)
    const orderedIds = next.map((blog) => mediaId(blog)).filter(Boolean)

    isReorderingRef.current = true
    setQuestBlogsSynced(next)
    setIsReorderBusy(true)
    if (typeof onSaveStateChange === 'function') {
      onSaveStateChange('saving')
    }

    try {
      await API.AdminAPI.quests.reorderQuestResources(
        questId,
        'blogs',
        orderedIds,
      )
      lastSavedOrderRef.current = getOrderKey(next)
      if (onBlogUpdate) {
        onBlogUpdate(next)
      }
      if (typeof onSaveStateChange === 'function') {
        onSaveStateChange('saved')
      }
    } catch (e) {
      Error.showError(e)
      if (typeof onSaveStateChange === 'function') {
        onSaveStateChange('error')
      }
      await restoreBlogsAfterReorderFailure(before)
    } finally {
      setIsReorderBusy(false)
      isReorderingRef.current = false
    }
  }

  const requestRemoveClick = (i) => {
    setPendingRemoveIndex(i)
  }

  const confirmRemoveClick = async () => {
    const i = pendingRemoveIndex
    setPendingRemoveIndex(null)
    if (typeof i === 'number') {
      await removeClick(i)
    }
  }

  const pendingRemoveLabel = truncateLabel(
    typeof pendingRemoveIndex === 'number'
      ? questBlogs[pendingRemoveIndex]?.title
      : '',
  )
  const pendingRemoveMessage = pendingRemoveLabel
    ? `\u201C${pendingRemoveLabel}\u201D will be detached from this experience.`
    : 'It will be detached from this experience.'

  const attachFromLibrary = async (blog) => {
    if (listDisabled) return
    const normalized = normalizeBlog(blog)
    const next = [...questBlogs, normalized]
    setQuestBlogsSynced(next)
    setLocalSearch('')
    try {
      await addClick(normalized, 'add')
      lastSavedOrderRef.current = getOrderKey(next)
      if (onBlogUpdate) {
        onBlogUpdate(next)
      }
    } catch (e) {
      setQuestBlogsSynced(questBlogs)
    }
  }

  const closeForm = () => {
    setShowCreate(false)
    setEditingId(null)
    setDraft(emptyBlogDraft())
    setFieldErrors(EMPTY_BLOG_FIELD_ERRORS)
    setSelectedImageFile(null)
    setShowImageCropModal(false)
  }

  const openCreateForm = () => {
    setEditingId(null)
    setDraft(emptyBlogDraft())
    setFieldErrors(EMPTY_BLOG_FIELD_ERRORS)
    setShowCreate(true)
  }

  const openEditForm = (blog) => {
    if (disabled) return
    setEditingId(mediaId(blog))
    setDraft({
      title: blog.title || '',
      author: blog.author || '',
      subTitle: blog.subTitle || '',
      type: blog.type || 'written',
      blog: blog.blog || '',
      blogLink: blog.blogLink || '',
      image: blog.image || '',
    })
    setFieldErrors(EMPTY_BLOG_FIELD_ERRORS)
    setShowCreate(true)
  }

  const uploadBlogImage = async (file) => {
    const contentType = file.name
      .substr(file.name.lastIndexOf('.') + 1)
      .toUpperCase()
    const { data } = await API.AdminAPI.quests.getBlogImage(contentType)
    if (!data) {
      throw new Error('Image upload failed')
    }
    await axios.put(data.url.url, file, {
      headers: { 'Content-Type': data.url.contentType },
    })
    return data.fileName
  }

  const openCropModal = (file) => {
    setSelectedImageFile(file)
    setShowImageCropModal(true)
  }

  const handleCropConfirm = async (croppedFile) => {
    if (!croppedFile) return
    setShowImageCropModal(false)
    setIsUploading(true)
    try {
      const fileName = await uploadBlogImage(croppedFile)
      setDraft((prev) => ({ ...prev, image: fileName }))
    } catch (e) {
      Error.showError(e)
    } finally {
      setIsUploading(false)
      setSelectedImageFile(null)
    }
  }

  const saveBlog = async () => {
    if (disabled || isSaving || isUploading) return

    const nextErrors = { ...EMPTY_BLOG_FIELD_ERRORS }
    if (!draft.title.trim()) {
      nextErrors.title = 'Required'
    }
    if (draft.type === 'link') {
      const link = draft.blogLink.trim()
      if (!link) {
        nextErrors.blogLink = 'Required'
      } else if (!isValidHttpUrl(link)) {
        nextErrors.blogLink = 'Invalid URL'
      }
    }

    setFieldErrors(nextErrors)
    if (nextErrors.title || nextErrors.blogLink) {
      return
    }

    setIsSaving(true)
    const payload = {
      title: draft.title.trim(),
      author: draft.author.trim(),
      subTitle: draft.subTitle.trim(),
      type: draft.type,
      blog: draft.type === 'written' ? draft.blog : '',
      blogLink: draft.type === 'link' ? draft.blogLink.trim() : '',
      image: draft.image || undefined,
    }
    try {
      if (editingId) {
        const { data } = await API.AdminAPI.quests.getUpdateBlog(
          editingId,
          payload,
        )
        const updated = normalizeBlog(data || { ...payload, _id: editingId })
        setAllBlogs((prev) =>
          prev.map((blog) =>
            mediaId(blog) === editingId ? { ...blog, ...updated } : blog,
          ),
        )
        const next = questBlogs.map((blog) =>
          mediaId(blog) === editingId ? { ...blog, ...updated } : blog,
        )
        setQuestBlogsSynced(next)
        if (onBlogUpdate) {
          onBlogUpdate(next)
        }
        Notification.showNotification('Blog updated.', 'success')
        closeForm()
      } else {
        const { data } = await API.AdminAPI.quests.getCreateBlog(payload)
        const created = normalizeBlog(data)
        setAllBlogs((prev) => [created, ...prev])
        await attachFromLibrary(created)
        Notification.showNotification('Blog created and attached.', 'success')
        closeForm()
      }
    } catch (e) {
      Error.showError(e)
    } finally {
      setIsSaving(false)
    }
  }

  const attachedBlogs = useMemo(
    () => questBlogs.filter((blog) => mediaId(blog)),
    [questBlogs],
  )

  const hasImage = Boolean(draft.image)
  const imageSrc = hasImage
    ? `${process.env.REACT_APP_IMG_SOURCE}/blogs/${draft.image}`
    : ''

  if (layout === 'picker') {
    const attachedLabel =
      attachedBlogs.length === 1
        ? '1 attached'
        : `${attachedBlogs.length} attached`

    return (
      <>
        <div className='experience-audio-picker'>
          <div className='experience-audio-picker-attached'>
            <div className='experience-ap-head'>
              <h4>On this experience</h4>
              <span className='experience-ap-count'>{attachedLabel}</span>
            </div>
            <div className='experience-ap-body'>
              {attachedBlogs.length === 0 ? (
                <div className='experience-ap-empty'>
                  <p>Nothing attached yet</p>
                  <small>Add reading from the library on the right.</small>
                </div>
              ) : (
                attachedBlogs.map((blog, index) => {
                  const id = mediaId(blog)
                  return (
                    <LearningAttachedDndRow
                      key={id}
                      dndType={BLOG_ROW_DND}
                      id={id}
                      index={index}
                      title={blog.title}
                      subtitle={blogSubtitle(blog)}
                      typeLabel='Article'
                      isEditing={editingId === id}
                      disabled={listDisabled}
                      onDragEnd={handleReorderDrop}
                      onOpen={() => openEditForm(blog)}
                      onRequestRemove={() =>
                        requestRemoveClick(
                          questBlogs.findIndex((b) => mediaId(b) === id),
                        )
                      }
                    />
                  )
                })
              )}
            </div>
          </div>
          <div className='experience-audio-picker-divider' aria-hidden='true' />
          <div className='experience-audio-picker-library'>
            <div className='experience-ap-head'>
              <h4>Library</h4>
            </div>
            <div className='experience-ap-body'>
              <div className='experience-lib-filter'>
                <input
                  type='text'
                  placeholder={placeholder}
                  value={localSearch}
                  onChange={(e) => setLocalSearch(e.target.value)}
                  autoComplete='off'
                />
              </div>
              {searchData.length === 0 ? (
                <div className='experience-lib-empty'>
                  {localSearch.trim()
                    ? 'No matching reading found.'
                    : 'All matching reading is already attached.'}
                </div>
              ) : (
                searchData.map((blog) => {
                  const id = mediaId(blog)
                  const subtitle = blogSubtitle(blog)
                  return (
                    <div className='experience-lib-item' key={id}>
                      <span className='copy'>
                        <b>{blog.title}</b>
                        {subtitle ? <small>{subtitle}</small> : null}
                      </span>
                      <button
                        type='button'
                        className='experience-lib-track-add'
                        disabled={listDisabled}
                        onClick={() => attachFromLibrary(blog)}>
                        Add
                      </button>
                    </div>
                  )
                })
              )}
            </div>
          </div>
        </div>
        {showCreate ? (
          <div aria-busy={isUploading || isSaving || undefined}>
            <div className='experience-blog-editor'>
              <div
                className='experience-f'
                style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  gap: 12,
                }}>
                <div>
                  <label>Title</label>
                  <input
                    className={classNames('experience-f-control', {
                      'is-invalid': Boolean(fieldErrors.title),
                    })}
                    type='text'
                    placeholder='Article title'
                    value={draft.title}
                    disabled={isUploading || isSaving}
                    onChange={(e) => {
                      const value = e.target.value
                      setDraft((prev) => ({ ...prev, title: value }))
                      if (fieldErrors.title) {
                        setFieldErrors((prev) => ({ ...prev, title: '' }))
                      }
                    }}
                  />
                  {fieldErrors.title ? (
                    <p className='experience-f-error mt-1 mb-0' role='alert'>
                      {fieldErrors.title}
                    </p>
                  ) : null}
                </div>
                <div>
                  <label>Author</label>
                  <input
                    className='experience-f-control'
                    type='text'
                    placeholder='Author name'
                    value={draft.author}
                    disabled={isUploading || isSaving}
                    onChange={(e) => {
                      const value = e.target.value
                      setDraft((prev) => ({ ...prev, author: value }))
                    }}
                  />
                </div>
              </div>
              <div className='experience-f mt-12px'>
                <label>Short description</label>
                <textarea
                  className='experience-f-control'
                  rows={2}
                  placeholder='One or two lines'
                  value={draft.subTitle}
                  disabled={isUploading || isSaving}
                  onChange={(e) => {
                    const value = e.target.value
                    setDraft((prev) => ({ ...prev, subTitle: value }))
                  }}
                />
              </div>
              <div className='experience-f mt-12px'>
                <ExperienceSelect
                  label='Type'
                  options={[
                    { value: 'written', label: 'Written' },
                    { value: 'link', label: 'Link' },
                  ]}
                  value={draft.type}
                  isSearchable={false}
                  disabled={isUploading || isSaving}
                  onChange={(e) => {
                    if (isUploading || isSaving) {
                      return
                    }
                    const value = e.target.value
                    setDraft((prev) => ({ ...prev, type: value }))
                    setFieldErrors((prev) => ({
                      ...prev,
                      blogLink: value === 'link' ? prev.blogLink : '',
                    }))
                  }}
                />
              </div>
              {draft.type === 'written' ? (
                <div className='experience-f mt-12px'>
                  <label>Body</label>
                  <ExperienceCKEditor
                    name='blog'
                    data={draft.blog || ''}
                    blog={true}
                    placeholder='Write the article'
                    disabled={isUploading || isSaving}
                    onReady={(editor) => {
                      if (editor) {
                        editor.editing.view.change((writer) => {
                          writer.setStyle(
                            'height',
                            '220px',
                            editor.editing.view.document.getRoot(),
                          )
                        })
                      }
                    }}
                    onChange={(event, editor) => {
                      if (isUploading || isSaving) {
                        return
                      }
                      const data = editor.getData()
                      setDraft((prev) => ({ ...prev, blog: data }))
                    }}
                  />
                </div>
              ) : (
                <div className='experience-f mt-12px'>
                  <label>Article URL</label>
                  <input
                    className={classNames('experience-f-control', {
                      'is-invalid': Boolean(fieldErrors.blogLink),
                    })}
                    type='url'
                    placeholder='https://'
                    value={draft.blogLink}
                    disabled={isUploading || isSaving}
                    onChange={(e) => {
                      const value = e.target.value
                      setDraft((prev) => ({
                        ...prev,
                        blogLink: value,
                      }))
                      if (fieldErrors.blogLink) {
                        setFieldErrors((prev) => ({ ...prev, blogLink: '' }))
                      }
                    }}
                  />
                  {fieldErrors.blogLink ? (
                    <p className='experience-f-error mt-1 mb-0' role='alert'>
                      {fieldErrors.blogLink}
                    </p>
                  ) : null}
                </div>
              )}
              <div className='experience-f mt-12px' style={{ maxWidth: 320 }}>
                <label>Image</label>
                <div className='experience-img-slot' data-ratio='2:1'>
                  <div
                    className={classNames('experience-img-frame', {
                      'is-empty': !hasImage && !isUploading,
                      'is-dragging': isDraggingImage,
                    })}
                    onClick={() => {
                      if (!isUploading && imageRef.current) {
                        imageRef.current.click()
                      }
                    }}
                    onDragEnter={(e) => {
                      e.preventDefault()
                      setIsDraggingImage(true)
                    }}
                    onDragOver={(e) => e.preventDefault()}
                    onDragLeave={(e) => {
                      e.preventDefault()
                      setIsDraggingImage(false)
                    }}
                    onDrop={(e) => {
                      e.preventDefault()
                      setIsDraggingImage(false)
                      const file = e.dataTransfer?.files?.[0]
                      if (file) openCropModal(file)
                    }}
                    role='button'
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault()
                        if (!isUploading && imageRef.current) {
                          imageRef.current.click()
                        }
                      }
                    }}>
                    {isUploading ? (
                      <span
                        className='experience-img-hover'
                        style={{ opacity: 1 }}>
                        Uploading…
                      </span>
                    ) : (
                      <span className='experience-img-hover'>
                        {isDraggingImage
                          ? 'Drop image to upload'
                          : hasImage
                          ? 'Click to replace'
                          : 'Upload image'}
                      </span>
                    )}
                    {hasImage ? (
                      <>
                        <Imgix
                          src={imageSrc}
                          imgixParams={{
                            fit: 'crop',
                            crop: 'center',
                            ar: isMDScreen ? '2:1' : '4:2',
                            auto: 'format,compress',
                          }}
                          width={800}
                          htmlAttributes={{
                            alt: 'blog',
                            style: {
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover',
                            },
                          }}
                          className='lazyload'
                        />
                        {!isUploading ? (
                          <button
                            type='button'
                            className='experience-img-remove'
                            aria-label='Remove image'
                            onClick={(e) => {
                              e.stopPropagation()
                              e.preventDefault()
                              setShowRemoveImageConfirm(true)
                            }}>
                            ×
                          </button>
                        ) : null}
                      </>
                    ) : !isUploading ? (
                      <>
                        <b>Upload image</b>
                        <small>JPG, JPEG or PNG · cropped to 2:1</small>
                      </>
                    ) : null}
                    <RBForm.Control
                      type='file'
                      accept='image/jpg, image/jpeg, image/png'
                      onChange={(e) => {
                        const target = e.target
                        const file = e.target.files?.[0]
                        if (file) {
                          openCropModal(file)
                        }
                        if (target) target.value = ''
                      }}
                      style={{ display: 'none' }}
                      ref={imageRef}
                    />
                  </div>
                </div>
              </div>
            </div>
            <div className='experience-form-actions mt-12px'>
              <ExperienceButton
                variant='primary'
                size='sm'
                isLoading={isSaving}
                disabled={disabled || isUploading}
                onClick={saveBlog}>
                Save blog
              </ExperienceButton>
              <ExperienceButton
                size='sm'
                disabled={isSaving || isUploading}
                onClick={closeForm}>
                Cancel
              </ExperienceButton>
            </div>
            <ImageCropModal
              show={showImageCropModal}
              onHide={() => {
                setShowImageCropModal(false)
                setSelectedImageFile(null)
              }}
              file={selectedImageFile}
              onConfirm={handleCropConfirm}
              isUploading={isUploading}
              aspect={isMDScreen ? 2 / 1 : 4 / 2}
              title='Crop Image'
            />
            <ExperienceConfirmModal
              show={showRemoveImageConfirm}
              onHide={() => setShowRemoveImageConfirm(false)}
              onConfirm={() => {
                setShowRemoveImageConfirm(false)
                setDraft((prev) => ({ ...prev, image: '' }))
              }}
              title='Remove this image?'
              message='The image will be cleared. You can upload another.'
              confirmLabel='Delete'
            />
          </div>
        ) : (
          <ExperienceButton
            size='sm'
            disabled={listDisabled}
            onClick={openCreateForm}>
            Add new blog
          </ExperienceButton>
        )}
        <ExperienceConfirmModal
          show={pendingRemoveIndex != null}
          onHide={() => setPendingRemoveIndex(null)}
          onConfirm={confirmRemoveClick}
          title='Remove this resource?'
          message={pendingRemoveMessage}
          confirmLabel='Delete'
        />
      </>
    )
  }

  return (
    <div className='experience-f'>
      {questBlogs.map((el, i) => (
        <div key={i} className='mb-2 position-relative'>
          <RBForm.Control
            type='text'
            placeholder={placeholder}
            onChange={(e) => {
              handleChange(i, e)
              setLocalSearch(e.target.value)
            }}
            onFocus={(e) => {
              setInputFocus(true)
              setCurrentIndex(i)
              setLocalSearch(e.target.value)
            }}
            onBlur={() => {
              setTimeout(() => {
                setInputFocus(false)
                setCurrentIndex(null)
                setLocalSearch('')
              }, 200)
            }}
            value={el?.title || ''}
            id='search-media'
            autoComplete='off'
            className={classNames(
              'd-inline experience-f-control black-color-placeholder',
              {
                'w-11/12': !disabled,
              },
            )}
            disabled={el?._id || disabled}
          />
          {searchData?.length > 0 &&
          inputFocus &&
          localSearch !== '' &&
          currentIndex === i ? (
            <ul className='search-media-list p-0'>
              {searchData?.map((blog) => (
                <React.Fragment key={mediaId(blog)}>
                  <li
                    onClick={() => {
                      let values = [...questBlogs]
                      values[i] = {
                        ...values[i],
                        ...normalizeBlog(blog),
                      }
                      setQuestBlogs([...values, {}])
                      setCurrentIndex(null)
                      setLocalSearch('')
                      addClick(blog, 'add')
                    }}
                    className='user-input'>
                    <strong>{blog?.title}</strong> / {blog?.subTitle}
                  </li>
                  <hr className='m-0' />
                </React.Fragment>
              ))}
            </ul>
          ) : null}
          {!disabled && el?._id && (
            <div className='w-1/12 text-center d-inline-block'>
              <button
                type='button'
                className='experience-x'
                aria-label='Remove blog'
                onClick={() => requestRemoveClick(i)}
                disabled={disabled}>
                <ExperienceDeleteIcon />
              </button>
            </div>
          )}
        </div>
      ))}
      <ExperienceConfirmModal
        show={pendingRemoveIndex != null}
        onHide={() => setPendingRemoveIndex(null)}
        onConfirm={confirmRemoveClick}
        title='Remove this resource?'
        message={pendingRemoveMessage}
        confirmLabel='Delete'
      />
    </div>
  )
}

export default QuestBlogs
